import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Shell } from './Shell';
import { LoginPage } from '../modules/auth/LoginPage';
import { SignupPage } from '../modules/auth/SignupPage';
import { ForgotPasswordPage } from '../modules/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../modules/auth/ResetPasswordPage';
import { SsoCompletePage } from '../modules/auth/SsoCompletePage';
import { ProtectedRoute } from './ProtectedRoute';
import { useStoredTheme, useThemeWatcher } from '../shared/hooks/useTheme';

// Eager: anything on the critical render path (login + shell).
// Lazy: every module page - they ship as separate chunks and load on first
// navigation, keeping the initial bundle small for faster app start.
const ContactsPage = lazy(() =>
  import('../modules/crm/ContactsPage').then((m) => ({ default: m.ContactsPage })),
);
const ContactDetailPage = lazy(() =>
  import('../modules/crm/ContactDetailPage').then((m) => ({ default: m.ContactDetailPage })),
);
const CompaniesPage = lazy(() =>
  import('../modules/crm/CompaniesPage').then((m) => ({ default: m.CompaniesPage })),
);
const CompanyDetailPage = lazy(() =>
  import('../modules/crm/CompanyDetailPage').then((m) => ({ default: m.CompanyDetailPage })),
);
const PipelinePage = lazy(() =>
  import('../modules/pipeline/PipelinePage').then((m) => ({ default: m.PipelinePage })),
);
const BoardsListPage = lazy(() =>
  import('../modules/kanban/BoardsListPage').then((m) => ({ default: m.BoardsListPage })),
);
const BoardPage = lazy(() =>
  import('../modules/kanban/BoardPage').then((m) => ({ default: m.BoardPage })),
);
const MyCardsPage = lazy(() =>
  import('../modules/kanban/MyCardsPage').then((m) => ({ default: m.MyCardsPage })),
);
const ContractsDashboardPage = lazy(() =>
  import('../modules/contracts/ContractsDashboardPage').then((m) => ({
    default: m.ContractsDashboardPage,
  })),
);
const ContractDetailPage = lazy(() =>
  import('../modules/contracts/ContractDetailPage').then((m) => ({ default: m.ContractDetailPage })),
);
const ReportsListPage = lazy(() =>
  import('../modules/reporting/ReportsListPage').then((m) => ({ default: m.ReportsListPage })),
);
const ReportBuilderPage = lazy(() =>
  import('../modules/reporting/ReportBuilderPage').then((m) => ({ default: m.ReportBuilderPage })),
);
const ReportViewerPage = lazy(() =>
  import('../modules/reporting/ReportViewerPage').then((m) => ({ default: m.ReportViewerPage })),
);
const AssessmentsListPage = lazy(() =>
  import('../modules/qualification/AssessmentsListPage').then((m) => ({
    default: m.AssessmentsListPage,
  })),
);
const AssessmentNewPage = lazy(() =>
  import('../modules/qualification/AssessmentNewPage').then((m) => ({ default: m.AssessmentNewPage })),
);
const AssessmentRunner = lazy(() =>
  import('../modules/qualification/AssessmentRunner').then((m) => ({ default: m.AssessmentRunner })),
);
const AssessmentReportView = lazy(() =>
  import('../modules/qualification/AssessmentReportView').then((m) => ({
    default: m.AssessmentReportView,
  })),
);
const SettingsPage = lazy(() =>
  import('../modules/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const DashboardPage = lazy(() =>
  import('../modules/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);

function RouteFallback() {
  return (
    <div className="h-full w-full grid place-items-center text-slate-500">
      <div className="flex items-center gap-3">
        <span
          className="inline-block h-4 w-4 rounded-full border-2 border-slate-300 border-t-brand-500 animate-spin"
          aria-hidden="true"
        />
        <span className="text-sm">Loading…</span>
      </div>
    </div>
  );
}

export function App() {
  // Apply the user's preferred theme immediately on boot from localStorage
  // and keep reacting to changes - useStoredTheme is reactive (subscribes to
  // a custom event the storeTheme setter dispatches), so the watcher always
  // sees the current preference instead of the value captured at first
  // render. Without this, switching theme in Settings would leave the App
  // watcher stuck on the previous OS preference.
  const stored = useStoredTheme();
  useThemeWatcher(stored);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Microsoft SSO web flow lands here after the server callback redirect. */}
        <Route path="/login/sso-complete" element={<SsoCompletePage />} />
        <Route
          element={
            <ProtectedRoute>
              <Shell />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<DashboardPage />} />

          {/* CRM */}
          <Route path="/crm/contacts" element={<ContactsPage />} />
          <Route path="/crm/contacts/:id" element={<ContactDetailPage />} />
          <Route path="/crm/companies" element={<CompaniesPage />} />
          <Route path="/crm/companies/:id" element={<CompanyDetailPage />} />

          {/* Pipeline */}
          <Route path="/pipeline" element={<PipelinePage />} />
          <Route path="/pipeline/:id" element={<PipelinePage />} />

          {/* Boards */}
          <Route path="/boards" element={<BoardsListPage />} />
          <Route path="/boards/:boardId" element={<BoardPage />} />
          <Route path="/my-cards" element={<MyCardsPage />} />

          {/* Contracts */}
          <Route path="/contracts" element={<ContractsDashboardPage />} />
          <Route path="/contracts/:id" element={<ContractDetailPage />} />

          {/* Reports */}
          <Route path="/reports" element={<ReportsListPage />} />
          <Route path="/reports/new" element={<ReportBuilderPage />} />
          <Route path="/reports/:id" element={<ReportViewerPage />} />

          {/* Qualification */}
          <Route path="/qualification" element={<AssessmentsListPage />} />
          <Route path="/qualification/new" element={<AssessmentNewPage />} />
          <Route path="/qualification/:id" element={<AssessmentRunner />} />
          <Route path="/qualification/:id/report" element={<AssessmentReportView />} />

          {/* Settings */}
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
